import { useMemo } from 'react'
import type { WorkspaceActivityRecord } from '../../../../services/activityService'
import type { AnalysisRowResolution, AnalysisTimeRangeKey } from '../../../../types/analysis'
import type {
  LiveModuleHeadline,
  LiveModuleRecord,
  LiveModuleSparkline,
} from '../LiveModuleCard'
import { bucketize, calculateDelta } from './bucketize'

const isSecurityAnomaly = (r: WorkspaceActivityRecord) => {
  const actType = (r.activityType || '').toUpperCase()
  const action = (r.action || '').toUpperCase()
  return (
    actType === 'SECURITY_ANOMALY' ||
    actType.includes('ANOMALY') ||
    actType.includes('SECURITY') ||
    action.includes('SECURITY') ||
    action.includes('UNAUTHORIZED') ||
    action.includes('ANOMALY') ||
    action.includes('SUSPICIOUS')
  )
}

export type UseSecurityCuesModuleParams = {
  workspaceId: string
  timeRange: AnalysisTimeRangeKey
  auditRecords: WorkspaceActivityRecord[]
  loading?: boolean
  error?: Error | null
  now?: Date
  resolutions?: Map<string, AnalysisRowResolution>
}

export const useSecurityCuesModule = ({
  timeRange,
  auditRecords,
  loading = false,
  error = null,
  now,
  resolutions,
}: UseSecurityCuesModuleParams) => {
  return useMemo(() => {
    const anomalies = auditRecords.filter(isSecurityAnomaly)
    const timestamps = anomalies.map((r) => r.createdAt)
    const points = bucketize(timestamps, timeRange, now)
    const delta = calculateDelta(points)

    const records: LiveModuleRecord[] = [...anomalies]
      .filter((item) => !resolutions?.has(`security-cues:${item.id}`))
      .sort((a, b) => {
        const timeA = a.createdAt instanceof Date ? a.createdAt.getTime() : 0
        const timeB = b.createdAt instanceof Date ? b.createdAt.getTime() : 0
        return timeB - timeA
      })
      .slice(0, 5)
      .map((r, index) => {
        const actor = r.actorName?.trim() || r.actorId || 'Unknown actor'
        const title = r.action || r.targetTitle?.trim() || 'Security anomaly'
        const secondary = r.targetTitle ? `${actor} · ${r.targetTitle}` : `${actor} · Watch`
        return {
          id: r.id || `security-cue-${index}`,
          primary: title,
          secondary,
          drillTarget: 'SUMMARY',
        }
      })

    const headline: LiveModuleHeadline = {
      value: anomalies.length,
      delta,
      label: 'anomalies',
    }

    const sparkline: LiveModuleSparkline = {
      points,
      direction: 'up-is-bad',
    }

    return {
      headline,
      sparkline,
      records,
      loading,
      error,
    }
  }, [auditRecords, timeRange, now, loading, error, resolutions])
}
